import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { typeLabels } from "@/mocks/movies";
import { formatViews, getMovieById, getRelatedMovies } from "@/lib/movieUtils";
import { useFavorites } from "@/lib/favorites";
import MovieGrid from "@/components/feature/MovieGrid";

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-background-200/50 last:border-b-0">
      <span className="w-16 shrink-0 text-sm text-foreground-500">{label}</span>
      <span className="text-sm text-foreground-900">{value}</span>
    </div>
  );
}

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { toggle, isFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const [showPlayNotice, setShowPlayNotice] = useState(false);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 免费观看视频 - 青青草视频`;
    }
    return () => {
      document.title = "青青草视频—高清影视资源在线观看与最新影视推荐";
    };
  }, [movie]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-foreground-500" />
        </span>
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          影视内容未找到或已下架
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          你访问的影视作品可能已被移除，或链接地址有误。
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 6);
  const favorite = isFavorite(movie.id);

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}/detail/${movie.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    toggle(movie.id);
    setToast(favorite ? "已取消收藏" : "已加入收藏");
  };

  return (
    <div className="w-full">
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium animate-fade-in">
          {toast}
        </div>
      )}

      <section className="relative w-full">
        <div className="absolute inset-0 h-[320px] md:h-[380px] overflow-hidden">
          <img
            src={movie.poster}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top blur-2xl scale-110 opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 to-background-50" />
        </div>

        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-10">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${movie.type}`}
              className="hover:text-primary-300 transition-colors cursor-pointer"
            >
              {typeLabels[movie.type]}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-900">{movie.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 高清海报`}
                  title={`${movie.title} 详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
                {movie.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-background-50">
                  {typeLabels[movie.type]}
                </span>
                {movie.rating > 0 && (
                  <span className="flex items-center gap-1 text-sm font-semibold text-accent-400">
                    <i className="ri-star-fill" />
                    {movie.rating.toFixed(1)}
                  </span>
                )}
                <span className="text-sm text-foreground-500">
                  {movie.year} · {movie.region} · {movie.duration}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${movie.type}?genre=${encodeURIComponent(genre)}`}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-900/60 text-secondary-200 hover:bg-secondary-800/70 transition-colors cursor-pointer"
                  >
                    {genre}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setShowPlayNotice((prev) => !prev)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-base" />
                  播放介绍
                </button>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border ${
                    favorite
                      ? "bg-accent-500/15 text-accent-300 border-accent-500/50"
                      : "bg-background-200/70 text-foreground-900 border-background-300/60 hover:bg-background-300/70"
                  }`}
                >
                  <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
                  {favorite ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-200/70 text-foreground-900 text-sm font-semibold border border-background-300/60 hover:bg-background-300/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
              </div>

              {showPlayNotice && (
                <div className="mt-4 rounded-lg bg-background-100 border border-primary-500/30 p-4 animate-fade-in">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-primary-400 text-lg mt-0.5" />
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      青青草视频为影视推荐与信息展示平台，本站不提供在线播放与下载服务。以上信息仅作内容介绍与推荐参考，感谢你的理解与支持。
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 pb-16 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <section className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {movie.synopsis}
              </p>
            </section>

            <section className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                影片数据
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <InfoRow label="类型" value={`${typeLabels[movie.type]} · ${movie.genres.join(" / ")}`} />
                <InfoRow label="年份" value={`${movie.year} 年`} />
                <InfoRow label="地区" value={movie.region} />
                <InfoRow label="片长" value={movie.duration} />
                <InfoRow label="导演" value={movie.director} />
                <InfoRow label="评分" value={movie.rating > 0 ? `${movie.rating.toFixed(1)} 分` : "暂无评分"} />
                <InfoRow label="播放" value={formatViews(movie.views)} />
                <InfoRow label="更新" value={movie.updatedAt} />
              </div>
            </section>

            <section className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                主要演职人员
              </h2>
              <div className="flex flex-wrap gap-3">
                {movie.cast.split(" / ").map((name) => (
                  <span
                    key={name}
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-200/60 text-sm text-foreground-900"
                  >
                    <span className="w-7 h-7 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-300 text-xs">
                      <i className="ri-user-line" />
                    </span>
                    {name}
                  </span>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-3">
                内容标签
              </h3>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/category/${movie.type}`}
                  className="px-3 py-1 rounded-md text-xs bg-background-200/70 text-foreground-700 hover:bg-background-300/70 transition-colors cursor-pointer"
                >
                  {typeLabels[movie.type]}
                </Link>
                <Link
                  to={`/category/${movie.type}?region=${encodeURIComponent(movie.region)}`}
                  className="px-3 py-1 rounded-md text-xs bg-background-200/70 text-foreground-700 hover:bg-background-300/70 transition-colors cursor-pointer"
                >
                  {movie.region}
                </Link>
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${movie.type}?genre=${encodeURIComponent(genre)}`}
                    className="px-3 py-1 rounded-md text-xs bg-accent-500/15 text-accent-300 hover:bg-accent-500/25 transition-colors cursor-pointer"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-950/70 border border-primary-800/40 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
                支持我们
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                把喜欢的作品加入收藏，方便随时回来查看推荐内容。
              </p>
              <button
                type="button"
                onClick={handleFavorite}
                className={`w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  favorite
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
                {favorite ? "已收藏该作品" : "加入我的收藏"}
              </button>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-10 rounded-lg bg-primary-500/15 flex items-center justify-center">
              <i className="ri-movie-2-line text-xl text-primary-400" />
            </span>
            <h2 className="font-heading text-lg md:text-2xl font-bold text-foreground-950">
              相关推荐
            </h2>
          </div>
          <MovieGrid
            movies={related}
            emptyText="暂无推荐内容，敬请期待"
            columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
          />
        </section>
      </div>
    </div>
  );
}